import { flushSync } from "react-dom";
import { matchRoutes, type createBrowserRouter } from "react-router-dom";
import { prefetchListRouteData } from "@/utils/routePrefetch";
import {
  HERO_ATTR,
  SHARED_KINDS,
  handoffPath,
  sharedAttr,
  sharedClass,
  sharedName,
  writeHandoff,
  type SharedKind,
} from "@/utils/sharedElements";

// Shared element page transitions on top of React Router's `viewTransition`.
//
// React Router starts the view transition; this module decides what morphs.
// A click on a link that contains marked elements (see sharedElements.ts)
// names those elements, then the update callback waits for the destination's
// hero to be in the DOM, names it the same, and only then lets the browser
// take the new snapshot. Names live on the elements only for the length of
// one transition, so no page ever has two elements with the same name.
//
// Everything here is event driven: no per frame work. The animation itself
// is the browser's, on the compositor.

type AppRouter = ReturnType<typeof createBrowserRouter>;
type AppLocation = AppRouter["state"]["location"];
type Finder = () => Element | null;

interface Pair {
  kind: SharedKind;
  name: string;
  from: Finder;
  to: Finder;
}

interface Plan {
  pairs: Pair[];
  /** Scroll to restore once the new page is in, for back and forward. */
  restoreY: number | null;
  /** How long the new page may take to render its hero before we give up on the morph. */
  waitMs: number;
}

interface SourceRef {
  keys: Partial<Record<SharedKind, string>>;
  index: number;
}

interface Intent extends SourceRef {
  path: string;
  elements: Partial<Record<SharedKind, Element>>;
  at: number;
}

interface HistoryLink extends SourceRef {
  toKey: string;
}

const PUSH_WAIT_MS = 400;
const POP_WAIT_MS = 500;
const INTENT_MAX_AGE_MS = 4000;
const MAX_REMEMBERED = 60;

let router: AppRouter | null = null;
let nativeStart: Document["startViewTransition"] | null = null;
let lastLocation: AppLocation | null = null;
let nextPlan: Plan | null = null;
let intent: Intent | null = null;
let running = 0;
let lenis: { resize?: () => void; reset?: () => void } | null = null;

const listeners = new Set<() => void>();
const scrollByKey = new Map<string, number>();
const historyLinks = new Map<string, HistoryLink>();
const preloadedPaths = new Set<string>();

const remember = <V>(map: Map<string, V>, key: string, value: V) => {
  map.delete(key);
  map.set(key, value);
  while (map.size > MAX_REMEMBERED) {
    const oldest = map.keys().next();
    if (oldest.done) break;
    map.delete(oldest.value);
  }
};

export function supportsViewTransitions(): boolean {
  return typeof document !== "undefined" && typeof document.startViewTransition === "function";
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const escapeAttr = (value: string) =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value.replace(/["\\]/g, "\\$&");

const isRendered = (element: Element | null): element is HTMLElement =>
  Boolean(element && element.isConnected && element instanceof HTMLElement && element.getClientRects().length > 0);

const findHero = (kind: SharedKind): Finder => () => {
  const heroes = document.querySelectorAll(`[${HERO_ATTR}~="${kind}"]`);
  for (const hero of Array.from(heroes)) if (isRendered(hero)) return hero;
  return null;
};

const sourcesFor = (kind: SharedKind, key: string) =>
  Array.from(document.querySelectorAll(`[${sharedAttr(kind)}="${escapeAttr(key)}"]`)).filter(isRendered);

/**
 * The same card that was clicked, or the closest match once the page has been
 * rebuilt: same position among cards with that key if the count still
 * matches, else the first one in view.
 */
const findSource = (ref: SourceRef, kind: SharedKind): Finder => () => {
  const key = ref.keys[kind];
  if (!key) return null;
  const anchorKind = SHARED_KINDS.find((candidate) => ref.keys[candidate]);
  if (anchorKind && anchorKind !== kind) {
    const anchor = findSource(ref, anchorKind)()?.closest("a");
    const inside = anchor?.querySelector(`[${sharedAttr(kind)}="${escapeAttr(key)}"]`);
    if (isRendered(inside)) return inside;
  }
  const candidates = sourcesFor(kind, key);
  if (candidates.length === 0) return null;
  const exact = candidates[ref.index];
  if (exact) return exact;
  const inView = candidates.find((element) => {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  });
  return inView || candidates[0];
};

function readSource(anchor: Element): Omit<Intent, "path" | "at"> | null {
  const elements: Partial<Record<SharedKind, Element>> = {};
  const keys: Partial<Record<SharedKind, string>> = {};
  for (const kind of SHARED_KINDS) {
    const attr = sharedAttr(kind);
    const element = anchor.hasAttribute(attr) ? anchor : anchor.querySelector(`[${attr}]`);
    const key = element?.getAttribute(attr);
    if (element && key) {
      elements[kind] = element;
      keys[kind] = key;
    }
  }
  const first = SHARED_KINDS.find((kind) => elements[kind]);
  if (!first) return null;
  const index = sourcesFor(first, keys[first] as string).indexOf(elements[first] as HTMLElement);
  return { elements, keys, index: Math.max(0, index) };
}

function imageOf(element: Element | undefined): HTMLImageElement | null {
  if (!element) return null;
  if (element instanceof HTMLImageElement) return element;
  return element.querySelector("img");
}

function internalAnchor(event: Event): HTMLAnchorElement | null {
  const target = event.target as Element | null;
  const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!anchor) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  if (anchor.hasAttribute("download")) return null;
  if (anchor.origin !== window.location.origin) return null;
  return anchor;
}

function onClick(event: MouseEvent) {
  intent = null;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = internalAnchor(event);
  if (!anchor) return;
  const source = readSource(anchor);
  if (!source) return;
  const artElement = source.elements.art || source.elements.stage || source.elements.avatar;
  const image = imageOf(artElement);
  const titleElement = source.elements.title;
  const firstKind = SHARED_KINDS.find((kind) => source.keys[kind]) as SharedKind;
  writeHandoff(anchor.pathname, {
    key: source.keys[firstKind] as string,
    kinds: SHARED_KINDS.filter((kind) => source.keys[kind]),
    artwork: image?.currentSrc || image?.src || artElement?.getAttribute("data-vt-src") || undefined,
    title: titleElement?.textContent?.trim() || image?.alt || undefined,
  });
  intent = { ...source, path: handoffPath(anchor.pathname), at: Date.now() };
}

function preloadPath(pathname: string) {
  if (!router || preloadedPaths.has(pathname)) return;
  preloadedPaths.add(pathname);
  if (preloadedPaths.size > 200) preloadedPaths.clear();
  for (const match of matchRoutes(router.routes, pathname) || []) {
    const preload = (match.route.handle as { preload?: () => unknown } | undefined)?.preload;
    if (typeof preload === "function") preload();
  }
  prefetchListRouteData(pathname);
}

function onIntentToNavigate(event: Event) {
  const anchor = internalAnchor(event);
  if (anchor) preloadPath(anchor.pathname);
}

function planFor(previous: AppLocation, next: AppLocation, action: string): Plan {
  const reduced = prefersReducedMotion();
  if (action !== "POP") {
    const current = intent;
    intent = null;
    if (!current || current.path !== handoffPath(next.pathname) || Date.now() - current.at > INTENT_MAX_AGE_MS) {
      return { pairs: [], restoreY: null, waitMs: 0 };
    }
    remember(historyLinks, previous.key, { keys: current.keys, index: current.index, toKey: next.key });
    const kinds = SHARED_KINDS.filter((kind) => current.keys[kind]);
    return {
      pairs: reduced
        ? []
        : kinds.map((kind) => ({
            kind,
            name: sharedName(kind, current.keys[kind] as string),
            from: () => current.elements[kind] || null,
            to: findHero(kind),
          })),
      restoreY: null,
      waitMs: PUSH_WAIT_MS,
    };
  }

  intent = null;
  const restoreY = scrollByKey.get(next.key) ?? 0;
  const back = historyLinks.get(next.key);
  const forward = historyLinks.get(previous.key);
  let pairs: Pair[] = [];
  if (back && back.toKey === previous.key) {
    pairs = SHARED_KINDS.filter((kind) => back.keys[kind]).map((kind) => ({
      kind,
      name: sharedName(kind, back.keys[kind] as string),
      from: findHero(kind),
      to: findSource(back, kind),
    }));
  } else if (forward && forward.toKey === next.key) {
    pairs = SHARED_KINDS.filter((kind) => forward.keys[kind]).map((kind) => ({
      kind,
      name: sharedName(kind, forward.keys[kind] as string),
      from: findSource(forward, kind),
      to: findHero(kind),
    }));
  }
  return { pairs: reduced ? [] : pairs, restoreY, waitMs: POP_WAIT_MS };
}

function waitFor(check: () => boolean, ms: number): Promise<void> {
  if (ms <= 0 || check()) return Promise.resolve();
  return new Promise((resolve) => {
    let timer = 0;
    const observer = new MutationObserver(() => {
      if (check()) finish();
    });
    function finish() {
      observer.disconnect();
      window.clearTimeout(timer);
      resolve();
    }
    observer.observe(document.body, { childList: true, subtree: true });
    timer = window.setTimeout(finish, ms);
  });
}

/** Scroll the page and tell Lenis, whose own target would otherwise pull it back. */
export function scrollPageTo(y: number) {
  window.scrollTo({ top: Math.max(0, y), left: 0, behavior: "instant" as ScrollBehavior });
  lenis?.resize?.();
  lenis?.reset?.();
}

const canReach = (y: number) => document.documentElement.scrollHeight - window.innerHeight >= y - 1;

let cancelRestore: (() => void) | null = null;

/**
 * Back to a saved position. A page rebuilt from cache can still be short for
 * a moment, so if the position is out of reach we try again as the page
 * grows, for a couple of seconds, unless the visitor starts scrolling.
 */
function restoreScroll(y: number) {
  cancelRestore?.();
  scrollPageTo(y);
  if (y <= 0 || canReach(y) || typeof ResizeObserver === "undefined") return;
  const observer = new ResizeObserver(() => {
    if (!canReach(y)) return;
    scrollPageTo(y);
    stop();
  });
  const timer = window.setTimeout(() => stop(), 2000);
  const inputs = ["wheel", "touchstart", "keydown", "pointerdown"];
  function stop() {
    observer.disconnect();
    window.clearTimeout(timer);
    inputs.forEach((type) => window.removeEventListener(type, stop, true));
    if (cancelRestore === stop) cancelRestore = null;
  }
  inputs.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
  observer.observe(document.body);
  cancelRestore = stop;
}

function notify() {
  listeners.forEach((listener) => listener());
}

function setName(element: HTMLElement, kind: SharedKind, name: string) {
  element.style.setProperty("view-transition-name", name);
  element.style.setProperty("view-transition-class", sharedClass(kind));
}

function clearNames(elements: Set<HTMLElement>) {
  elements.forEach((element) => {
    element.style.removeProperty("view-transition-name");
    element.style.removeProperty("view-transition-class");
  });
  elements.clear();
}

function namePairs(plan: Plan, side: "from" | "to", named: Set<HTMLElement>) {
  const used = new Set<Element>();
  for (const pair of plan.pairs) {
    const element = pair[side]();
    if (!isRendered(element) || used.has(element)) continue;
    used.add(element);
    setName(element, pair.kind, pair.name);
    named.add(element);
  }
}

function start(update: () => unknown, plan: Plan | null): ViewTransition {
  const named = new Set<HTMLElement>();
  const root = document.documentElement;
  if (plan?.pairs.length) namePairs(plan, "from", named);
  root.dataset.vt = named.size ? "shared" : "page";

  const transition = (nativeStart as Document["startViewTransition"]).call(document, async () => {
    await update();
    if (!plan) return;
    const primary = plan.pairs[0];
    const restoreY = plan.restoreY;
    await waitFor(
      () => (!primary || isRendered(primary.to())) && (restoreY === null || canReach(restoreY)),
      primary || restoreY ? plan.waitMs : 0,
    );
    if (restoreY !== null) restoreScroll(restoreY);
    clearNames(named);
    if (plan.pairs.length) namePairs(plan, "to", named);
  });

  running += 1;
  notify();
  const done = () => {
    clearNames(named);
    running = Math.max(0, running - 1);
    if (running === 0) delete root.dataset.vt;
    notify();
  };
  transition.finished.then(done, done);
  // A skipped transition (a second click mid flight) rejects `ready`; that is
  // expected, so keep it out of the console.
  transition.ready.catch(() => undefined);
  return transition;
}

function wrappedStart(this: Document, arg?: ViewTransitionUpdateCallback | StartViewTransitionOptions): ViewTransition {
  const update = typeof arg === "function" ? arg : arg?.update;
  const plan = nextPlan;
  nextPlan = null;
  return start(() => update?.(), plan);
}

export interface SharedPair {
  kind: SharedKind;
  key: string;
  from: Element | null | (() => Element | null);
  to: () => Element | null;
}

/**
 * A same page morph, for things that open in place (a video card into the
 * player). `update` runs inside flushSync so the new state is in the DOM
 * before the snapshot. Without the API it just runs the update.
 */
export function runSharedTransition(update: () => void, pairs: SharedPair[]): void {
  if (!nativeStart || prefersReducedMotion()) {
    update();
    return;
  }
  const plan: Plan = {
    pairs: pairs
      .filter((pair) => pair.key)
      .map((pair) => ({
        kind: pair.kind,
        name: sharedName(pair.kind, pair.key),
        from: typeof pair.from === "function" ? pair.from : () => pair.from as Element | null,
        to: pair.to,
      })),
    restoreY: null,
    waitMs: PUSH_WAIT_MS,
  };
  start(() => flushSync(update), plan);
}

/** The rendered destination element of a kind, e.g. the open video player. */
export function sharedHero(kind: SharedKind): Element | null {
  return findHero(kind)();
}

/** A rendered source element with this key, the one in view first. */
export function sharedSource(kind: SharedKind, key: string): Element | null {
  return findSource({ keys: { [kind]: key }, index: -1 }, kind)();
}

export function isViewTransitionRunning(): boolean {
  return running > 0;
}

export function subscribeViewTransitions(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setScrollDriver(instance: { resize?: () => void; reset?: () => void } | null) {
  lenis = instance;
}

/**
 * Where a new page should start: the top after a push, the saved position
 * after back or forward. Called by App on every pathname change; the view
 * transition path applies it again once the new page has its content.
 */
export function scrollForNavigation(location: { key: string }, action: string) {
  if (action === "POP") restoreScroll(scrollByKey.get(location.key) ?? 0);
  else {
    cancelRestore?.();
    scrollPageTo(0);
  }
}

export function installViewTransitions(appRouter: AppRouter) {
  if (router || typeof window === "undefined") return;
  router = appRouter;
  lastLocation = appRouter.state.location;
  if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

  appRouter.subscribe((state, opts) => {
    const next = state.location;
    const previous = lastLocation;
    if (!previous || next === previous) return;
    lastLocation = next;
    cancelRestore?.();
    remember(scrollByKey, previous.key, window.scrollY);
    nextPlan = nativeStart && opts?.viewTransitionOpts ? planFor(previous, next, state.historyAction) : null;
    if (!nextPlan) intent = null;
  });

  document.addEventListener("click", onClick, true);
  document.addEventListener("pointerover", onIntentToNavigate, { capture: true, passive: true });
  document.addEventListener("focusin", onIntentToNavigate, true);
  document.addEventListener("touchstart", onIntentToNavigate, { capture: true, passive: true });

  if (supportsViewTransitions()) {
    nativeStart = document.startViewTransition;
    document.startViewTransition = wrappedStart as Document["startViewTransition"];
  }
}
